import type { DocumentEvidenceReference } from "@repo/shared/document-evidence-link";
import { DEFAULT_DOCUMENT_EVIDENCE_LABEL } from "./evidence-runs";

function normalizeValue(value: string | null): string {
  return value?.trim() ?? "";
}

function readOptionalInteger(value: string | null): number | null {
  if (value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export function findDocumentEvidenceElement(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }

  const evidenceElement = target.closest<HTMLElement>('[data-type="documentEvidence"]');
  return evidenceElement instanceof HTMLElement ? evidenceElement : null;
}

export function readDocumentEvidenceReferenceFromElement(
  element: Element | null | undefined
): DocumentEvidenceReference | null {
  if (!element) return null;

  const artifactId = normalizeValue(element.getAttribute("data-artifact-id"));
  const processingRevisionId = normalizeValue(element.getAttribute("data-processing-revision-id"));
  const blockId = normalizeValue(element.getAttribute("data-block-id"));
  if (!artifactId || !processingRevisionId || !blockId) {
    return null;
  }

  const startChar = readOptionalInteger(element.getAttribute("data-start-char"));
  const endChar = readOptionalInteger(element.getAttribute("data-end-char"));
  const hasNoSpan = startChar === null && endChar === null;
  const hasValidSpan =
    startChar !== null && endChar !== null && startChar >= 0 && startChar < endChar;
  if (!hasNoSpan && !hasValidSpan) {
    return null;
  }

  return {
    kind: "document_block",
    artifactId,
    processingRevisionId,
    blockId,
    ...(hasValidSpan ? { span: { startChar, endChar } } : {}),
  };
}

/**
 * Marks the plugin-built marker that stands in for a hoisted citation-only paragraph. It
 * carries the same locator contract as a real marker but backs no node in the document.
 */
export const EVIDENCE_RUN_PROXY_ATTRIBUTE = "data-evidence-run-proxy";

const REQUIRED_LOCATOR_ATTRIBUTES = [
  "data-artifact-id",
  "data-processing-revision-id",
  "data-block-id",
] as const;

const OPTIONAL_LOCATOR_ATTRIBUTES = ["data-start-char", "data-end-char"] as const;

function escapeAttributeValue(value: string): string {
  return value.replace(/(["\\])/g, "\\$1");
}

/**
 * A selector matching every marker that cites exactly the passage `element` cites, either
 * the proxies for it or the real markers behind them. Absent span attributes are matched as
 * absent, so a whole-block citation never matches a marker that cites a span of it.
 * `null` when the element carries no usable locator.
 */
export function buildDocumentEvidenceLocatorSelector(
  element: Element,
  options: { proxy: boolean }
): string | null {
  const parts = [
    '[data-type="documentEvidence"]',
    options.proxy ? `[${EVIDENCE_RUN_PROXY_ATTRIBUTE}]` : `:not([${EVIDENCE_RUN_PROXY_ATTRIBUTE}])`,
  ];

  for (const attribute of REQUIRED_LOCATOR_ATTRIBUTES) {
    const value = normalizeValue(element.getAttribute(attribute));
    if (!value) return null;
    parts.push(`[${attribute}="${escapeAttributeValue(value)}"]`);
  }
  for (const attribute of OPTIONAL_LOCATOR_ATTRIBUTES) {
    const value = normalizeValue(element.getAttribute(attribute));
    parts.push(value ? `[${attribute}="${escapeAttributeValue(value)}"]` : `:not([${attribute}])`);
  }

  return parts.join("");
}

/**
 * A stable identity for a citation marker that carries no locator attributes of its own —
 * the chat chips, whose citations live in React props. The provider re-finds the marker by
 * this key when its DOM node is replaced under an open popover (a streaming transcript
 * re-renders the message the reader cited from).
 */
export const EVIDENCE_ANCHOR_KEY_ATTRIBUTE = "data-evidence-anchor-key";

/**
 * Whether a popover may still be positioned against `element`: attached to the document
 * and rendered. `checkVisibility` is absent in jsdom, where layout does not exist and every
 * attached element counts as visible.
 */
export function isEvidenceAnchorLive(element: Element): boolean {
  if (!element.isConnected) return false;
  return typeof element.checkVisibility === "function" ? element.checkVisibility() : true;
}

function liveMatches(root: ParentNode, selector: string): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(selector)].filter(isEvidenceAnchorLive);
}

/**
 * The selectors, in order of preference, that identify the markers standing for the same
 * citation as `element`: a chat chip by its anchor key; an editor marker by its locator —
 * first markers of its own kind, then, for a real marker that is hidden where it is stored
 * (a hoisted citation-only paragraph), the proxy drawn for it.
 */
function buildEvidenceAnchorSelectors(element: Element): string[] {
  const anchorKey = element.getAttribute(EVIDENCE_ANCHOR_KEY_ATTRIBUTE);
  if (anchorKey) {
    return [`[${EVIDENCE_ANCHOR_KEY_ATTRIBUTE}="${escapeAttributeValue(anchorKey)}"]`];
  }
  const isProxy = element.hasAttribute(EVIDENCE_RUN_PROXY_ATTRIBUTE);
  const own = buildDocumentEvidenceLocatorSelector(element, { proxy: isProxy });
  if (!own) return [];
  const proxy = isProxy ? null : buildDocumentEvidenceLocatorSelector(element, { proxy: true });
  return proxy ? [own, proxy] : [own];
}

/**
 * A live marker standing for the same citation as `element`, searched under `root`:
 * `element` itself while it is live, otherwise its replacement — the marker React or
 * ProseMirror drew in its place after re-rendering the surface. When the same citation is
 * drawn several times (the same passage cited in two messages), the replacement is chosen by
 * `ordinal`, the position `element` held among the live matches when it was last seen.
 * `null` when nothing stands for the citation any more.
 */
export function findLiveEvidenceAnchor(
  element: HTMLElement,
  root: ParentNode,
  ordinal = 0
): HTMLElement | null {
  if (isEvidenceAnchorLive(element)) return element;
  for (const selector of buildEvidenceAnchorSelectors(element)) {
    const matches = liveMatches(root, selector);
    const match = matches[ordinal] ?? matches[0];
    if (match) return match;
  }
  return null;
}

/**
 * The position `element` holds among the live markers standing for its citation — what
 * {@link findLiveEvidenceAnchor} needs to pick the same one again after a re-render.
 */
export function readEvidenceAnchorOrdinal(element: HTMLElement, root: ParentNode): number {
  const [selector] = buildEvidenceAnchorSelectors(element);
  if (!selector) return 0;
  return Math.max(0, liveMatches(root, selector).indexOf(element));
}

export function readDocumentEvidenceLabelFromElement(element: Element | null | undefined): string {
  return (
    normalizeValue(element?.getAttribute("data-evidence-label") ?? null) ||
    DEFAULT_DOCUMENT_EVIDENCE_LABEL
  );
}

export function isDocumentEvidenceAvailable(element: Element | null | undefined): boolean {
  return element?.getAttribute("data-evidence-available") === "true";
}

/**
 * A chip is only "unavailable" once the authorized presentation has answered. While the
 * batched presentation query is still loading, `data-evidence-available` is absent and
 * the chip is neither available nor unavailable.
 */
export function isDocumentEvidenceUnavailable(element: Element | null | undefined): boolean {
  return element?.getAttribute("data-evidence-available") === "false";
}

export const UNAVAILABLE_DOCUMENT_EVIDENCE_TITLE = "Referenced passage is no longer available";
